import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCreateMealPlan, useAddMealPlanItem, mealPlanKeys } from './useMealPlans';
import { getWeekStart } from '@/utils/dateUtils';

interface ScheduleMealRequest {
  recipeId: string;
  dateISO: string;
  service: 'comida' | 'cena';
  servings?: number;
}

/**
 * Hook to ensure meal plan exists and add recipe to it
 * Leverages backend's idempotent meal plan creation
 */
export function useEnsureMealPlan() {
  const queryClient = useQueryClient();
  const createMealPlan = useCreateMealPlan();
  const addMealPlanItem = useAddMealPlanItem();

  return useMutation({
    mutationFn: async ({ recipeId, dateISO, service, servings }: ScheduleMealRequest) => {
      try {
        // Calculate week start (Monday) for the given date
        const weekStart = getWeekStart(new Date(dateISO)).toISOString();
        
        // Create or get existing meal plan (backend is idempotent)
        const mealPlan = await createMealPlan.mutateAsync({ weekStart });
        
        // Add recipe to meal plan
        const mealPlanItem = await addMealPlanItem.mutateAsync({
          mealPlanId: mealPlan.id,
          data: { recipeId, day: dateISO, service, servings },
        });

        return { mealPlan, mealPlanItem };
      } catch (error: any) {
        // Enhance error with context
        if (error?.message?.includes('Ya existe una receta')) {
          throw new Error(`Este slot ya está ocupado. ${error.message}`);
        }
        throw error;
      }
    },
    onSuccess: (data) => {
      // Invalidate relevant queries to refresh UI
      const weekStartISO = getWeekStart(new Date(data.mealPlanItem.day)).toISOString().split('T')[0];
      queryClient.invalidateQueries({ queryKey: mealPlanKeys.week(weekStartISO) });
      queryClient.invalidateQueries({ queryKey: mealPlanKeys.detail(data.mealPlan.id) });
      queryClient.invalidateQueries({ queryKey: mealPlanKeys.current() });
    },
  });
}
